import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { HrLeaveBalance, HrLeaveRequest, HrLeaveType } from '../../../hr/hr.models';
import { MyHrLeaveWorkspace } from '../shared/my-hr-workspace.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { labelize, statusSeverity, toDateInputValue } from '../shared/my-hr-workspace.utils';

@Component({
  selector: 'app-my-hr-leave',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    PageDataPanelComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './my-hr-leave.component.html',
  styleUrls: ['./my-hr-leave.component.scss']
})
export class MyHrLeaveComponent implements OnInit {
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);

  readonly canRequestLeave = this.permissionService.hasPermission(app_permissions.hr.leave.request);

  workspace?: MyHrLeaveWorkspace;
  submitting = false;
  requestForm: {
    leaveTypeId?: number;
    startDate?: Date | null;
    endDate?: Date | null;
    reason?: string;
    emergencyLeave: boolean;
  } = {
    emergencyLeave: false
  };

  ngOnInit(): void {
    this.load();
  }

  get leaveTypeOptions(): Array<{ label: string; value: number | undefined }> {
    return (this.workspace?.leaveTypes || []).map(type => ({
      label: `${type.leaveName || type.leaveCode}`,
      value: type.leaveTypeId
    }));
  }

  get selectedType(): HrLeaveType | undefined {
    return this.workspace?.leaveTypes.find(type => type.leaveTypeId === this.requestForm.leaveTypeId);
  }

  get selectedBalance(): HrLeaveBalance | undefined {
    return this.workspace?.leaveBalances.find(balance => balance.leaveTypeId === this.requestForm.leaveTypeId);
  }

  statusSeverity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    return statusSeverity(value);
  }

  label(value?: string): string {
    return labelize(value);
  }

  submit(): void {
    const employeeId = this.workspace?.profile?.employeeId;
    if (!this.canRequestLeave || !employeeId || !this.requestForm.leaveTypeId || !this.requestForm.startDate || !this.requestForm.endDate) {
      this.messageService.add({ severity: 'warn', summary: 'Leave', detail: 'Leave type, start date, and end date are required.' });
      return;
    }

    const request: HrLeaveRequest = {
      leaveTypeId: this.requestForm.leaveTypeId,
      startDate: toDateInputValue(this.requestForm.startDate),
      endDate: toDateInputValue(this.requestForm.endDate),
      reason: this.requestForm.reason,
      emergencyLeave: this.requestForm.emergencyLeave
    };

    this.submitting = true;
    this.workspaceService.submitLeaveRequest(employeeId, request).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Submitted', detail: 'Leave request submitted.' });
        this.requestForm = { emergencyLeave: false };
        this.workspaceService.requestRefresh();
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Leave', detail: 'Leave request could not be submitted.' });
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  private load(): void {
    this.workspaceService.loadLeaveWorkspace().subscribe({
      next: workspace => {
        this.workspace = workspace;
      },
      error: () => {
        this.workspace = undefined;
      }
    });
  }
}
